import React, { useCallback, useEffect, useImperativeHandle, forwardRef, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import RuleFlowRail from './RuleFlowRail';
import RuleFlowCanvas, { type RuleFlowCanvasHandle } from './RuleFlowCanvas';
import {
  listChannelRuleFlows,
  parseChannelRuleFlows,
  renameFlow,
  RuleFlowParseUnavailableError,
  RuleFlowRenameNotFoundError,
  type RuleFlow,
} from '../../../services/channelRuleFlowsService';
import { getFlowRunMetrics, type FlowRun } from '../../../services/channelDashboardService';

export interface ChannelRuleFlowsViewProps {
  channelId: string;
  channelBusinessRules?: string;
  /** Forwarded straight through to RuleFlowCanvas — see its own prop doc. */
  onBusinessRulesChanged?: (updatedText: string) => void;
  /** business_rules_workflow_count — forwarded straight through to RuleFlowCanvas's header
   * "Runs: N" label; see its own prop doc. */
  onOpenDashboard?: () => void;
}

/** FIX 11b (frontend handover) — lets ChannelSettings.tsx gate its own navigation points (drawer
 * tab-switch, drawer close) on this view's currently-mounted flow having unsaved structural
 * edits, the same way this view already gates its own rail row-click below. */
export interface ChannelRuleFlowsViewHandle {
  hasUnsavedChanges: () => boolean;
  /** Runs the same guard dialog this view uses for its own rail row-click, then calls
   * onProceed() if the admin chooses to continue (immediately if nothing is dirty). */
  guardNavigation: (onProceed: () => void) => void;
}

/**
 * Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §8.1/§8.2) — the channel-scoped
 * view, rendered by WorkflowCanvasTab.tsx when a channelId is present. No separate card-grid
 * landing page (ruled out explicitly — an extra click-through step): on mount, list flows and,
 * if any are missing or stale, chain straight into a parse — all inside ONE continuous loading
 * state (TDD §2 rows 4-7) — then render the persistent rail + canvas immediately, with the
 * first flow already selected. The admin never clicks to "open" a flow for the common case.
 */
const ChannelRuleFlowsView = forwardRef<ChannelRuleFlowsViewHandle, ChannelRuleFlowsViewProps>(({ channelId, channelBusinessRules, onBusinessRulesChanged, onOpenDashboard }, ref) => {
  const [flows, setFlows] = useState<RuleFlow[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // business_rules_workflow_count — fetched independently of flows/loading above: a failure or
  // slow response here must never block or delay the flow list/canvas rendering, so it's plain
  // best-effort state with no loading gate of its own. Keyed by flow_id for O(1) lookup per row.
  const [flowRunsByFlowId, setFlowRunsByFlowId] = useState<Record<string, FlowRun>>({});
  // FIX 11b — tracks the currently-mounted flow's own dirty state (RuleFlowCanvas remounts via
  // key={flow.id} on every switch, so this only ever reflects the flow presently on screen). A
  // ref, not state: read synchronously inside click handlers, never needs its own re-render.
  const isDirtyRef = useRef(false);
  const canvasRef = useRef<RuleFlowCanvasHandle>(null);
  // Holds the "continue" action (switch rail row / switch tab / close drawer) while the unsaved-
  // changes dialog is open; null means the dialog is closed.
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [isGuardSaving, setIsGuardSaving] = useState(false);
  // Snapshotted the instant the dialog opens (from RuleFlowCanvas's own live pendingDiffs via its
  // handle) so the dialog can name what's actually unsaved instead of a generic "you have
  // changes" line — fixes exactly that ask.
  const [pendingSummaries, setPendingSummaries] = useState<string[]>([]);

  const guardNavigation = useCallback((onProceed: () => void) => {
    if (!isDirtyRef.current) {
      onProceed();
      return;
    }
    setPendingSummaries(canvasRef.current?.getPendingSummaries() ?? []);
    setPendingNavigation(() => onProceed);
  }, []);

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => isDirtyRef.current,
    guardNavigation,
  }), [guardNavigation]);

  const handleGuardCancel = useCallback(() => setPendingNavigation(null), []);

  const handleGuardDiscard = useCallback(() => {
    canvasRef.current?.discardEdits();
    const proceed = pendingNavigation;
    setPendingNavigation(null);
    proceed?.();
  }, [pendingNavigation]);

  const handleGuardSave = useCallback(async () => {
    setIsGuardSaving(true);
    const ok = await canvasRef.current?.saveEdits();
    setIsGuardSaving(false);
    const proceed = ok ? pendingNavigation : null;
    // On failure, close this dialog either way (don't navigate) so RuleFlowCanvas's own
    // persistError banner underneath is visible and the admin can retry with its normal Save
    // button — a stacked dialog would otherwise hide that explanation.
    setPendingNavigation(null);
    proceed?.();
  }, [pendingNavigation]);
  const [isReparsing, setIsReparsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when a rename hit a 404 (the flow was replaced by a re-parse): the list is reloaded, which
  // swaps this whole view to its loading state and back, so the explanation lives here (rendered
  // as a snackbar in the main layout) rather than in the rail's own editor, which gets unmounted.
  const [renameNotice, setRenameNotice] = useState<string | null>(null);
  // PR #635 review (L2) — true while RuleFlowCanvas has an edit's serialize -> save -> confirm
  // sequence in flight. Disables "Re-load" for that window so a fresh parse can't swap in a
  // new flow (new id -> RuleFlowCanvas remounts via key={flow.id}) while that edit is still
  // running against the flow it started on.
  const [isEditPersisting, setIsEditPersisting] = useState(false);
  const flowsRef = useRef<RuleFlow[]>([]);
  useEffect(() => {
    flowsRef.current = flows;
  }, [flows]);
  const hasCheckedStalenessOnMountRef = useRef(false);
  // Guards loadFlows' own setState calls against a stale in-flight call (e.g. channelId changes,
  // or the component unmounts, while a previous loadFlows() is still awaiting a response) —
  // same request-id idiom already used elsewhere in this codebase (e.g.
  // BusinessRulesSection.tsx's suggestRequestIdRef) rather than a boolean "cancelled" flag,
  // since loadFlows is also called directly by the "Try again" button, not just the mount effect.
  const loadRequestIdRef = useRef(0);

  const loadFlows = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      let result = await listChannelRuleFlows(channelId);
      if (requestId !== loadRequestIdRef.current) return;

      // TDD §2 row 6: missing OR stale at mount both auto-parse, inside this same loading
      // state — no intermediate "0 flows, click to generate" screen either way. Only checked
      // once per mount (hasCheckedStalenessOnMountRef) — staleness discovered later, while the
      // canvas is already open, is handled reactively by RuleFlowCanvas's serialize 409
      // instead (TDD §8.2 Finding 2) — never by this effect re-running.
      const needsParse = result.length === 0 || result.some((f) => f.is_stale);
      if (needsParse && !hasCheckedStalenessOnMountRef.current) {
        hasCheckedStalenessOnMountRef.current = true;
        result = await parseChannelRuleFlows(channelId);
        if (requestId !== loadRequestIdRef.current) return;
      }

      setFlows(result);
      setSelectedFlowId((prev) => prev ?? result[0]?.id ?? null);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      // PR #635 review (M1) — parse 422s when this channel has no business rules configured
      // yet, the single most common cold-start case for this tab (reachable directly from
      // ChannelSettings' tab bar, independent of "See workflow canvas"). Treat it exactly like
      // "zero flows" so the friendly, already-written empty-state copy below actually fires,
      // instead of surfacing the raw backend error string in a red Alert.
      if (err instanceof RuleFlowParseUnavailableError) {
        setFlows([]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load business rule flows.');
      }
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    void loadFlows();
  }, [loadFlows]);

  // business_rules_workflow_count — 'all' (lifetime) since this view has no time-filter control
  // of its own (unlike the Channel Operations dashboard); best-effort and isolated from loadFlows
  // above so a failure here never surfaces as the flow-list error state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getFlowRunMetrics(channelId, 'all');
        if (cancelled) return;
        const map: Record<string, FlowRun> = {};
        for (const run of data.flow_runs) map[run.flow_id] = run;
        setFlowRunsByFlowId(map);
      } catch (err) {
        console.error('Failed to load flow run metrics', err);
        if (!cancelled) setFlowRunsByFlowId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const handleReparse = async () => {
    setIsReparsing(true);
    setError(null);
    try {
      // force: true — PR review roosterhr/shay-email-ml#627 (M1). This is the button an admin
      // clicks specifically to force a fresh re-derivation (e.g. after an ORCH-side prompt
      // improvement, or just to double-check current behavior) — without force, ORCH's reuse
      // gate would silently return the same saved flows whenever the rules text hasn't changed,
      // making this button appear to do nothing with no way to tell why. The automatic on-mount
      // parse in loadFlows() above intentionally leaves force at its default (false): that path
      // exists to fill in missing/stale flows, not to force a redo of ones that are already fine.
      const result = await parseChannelRuleFlows(channelId, true);
      setFlows(result);
      // Keep the same flow selected by name if it still exists after re-parsing (names are
      // stable across a re-parse even though ids may not be); otherwise fall back to the first.
      setSelectedFlowId((prevId) => {
        const prevFlow = flowsRef.current.find((f) => f.id === prevId);
        const stillExists = prevFlow && result.find((f) => f.flow_name === prevFlow.flow_name);
        return stillExists ? stillExists.id : result[0]?.id ?? null;
      });
    } catch (err) {
      if (err instanceof RuleFlowParseUnavailableError) {
        setFlows([]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to re-load business rules.');
      }
    } finally {
      setIsReparsing(false);
    }
  };

  // Rename touches only flow_name (shay-email-backend#564), so — unlike a rail row-click — it is
  // deliberately NOT routed through guardNavigation's unsaved-changes dialog: the open flow's
  // pending step edits live in RuleFlowCanvas's own state and are unaffected. Only the renamed
  // flow is swapped in `flows`; selectedFlowId never changes, and RuleFlowCanvas (keyed by
  // flow.id, seeded once from `flow`) neither remounts nor re-seeds, so those edits survive.
  // Resolves on success; throws on any failure so the rail keeps its editor open with the message.
  const handleRenameFlow = async (flowId: string, newName: string): Promise<void> => {
    try {
      const updated = await renameFlow(channelId, flowId, newName);
      setFlows((prev) => prev.map((f) => (f.id === flowId ? updated : f)));
    } catch (err) {
      if (err instanceof RuleFlowRenameNotFoundError) {
        // A Re-load / re-parse replaces flow ids, so this id is gone: reload the list and let the admin
        // retry on the fresh row. (loadFlows only auto-parses if flows are missing or stale and it
        // hasn't already parsed during this mount.)
        setRenameNotice('That flow no longer exists — the list was reloaded. Please try renaming it again.');
        await loadFlows();
        return;
      }
      throw err;
    }
  };

  // Rendered on EVERY screen this view can be on. A 404 reload swaps the view to its loader and — if
  // the reload fails or comes back empty — to the error / empty screen, so the explanation can't
  // live only in the main layout. (Fixed-position, so it adds nothing to any layout.)
  const renameNoticeSnackbar = (
    <Snackbar
      open={renameNotice !== null}
      autoHideDuration={8000}
      // Not on 'clickaway': the admin's very next click (e.g. on the row, to retry) would otherwise
      // dismiss the explanation before it can be read; it still closes on its timer.
      onClose={(_event, reason) => {
        if (reason === 'clickaway') return;
        setRenameNotice(null);
      }}
      message={renameNotice ?? ''}
    />
  );

  if (loading) {
    // minHeight (matching this component's own main-content row further below) guards the same
    // height: '100%' from collapsing to the tab's top instead of truly centering — an ancestor
    // isn't guaranteed to already resolve to a definite height on its own.
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 350,
          gap: 1.5,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading Business Rules Workflow...
        </Typography>
        {renameNoticeSnackbar}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          // PR #635 review (L1) — the only way to recover used to be leaving the Workflow tab
          // and coming back (re-triggering the mount effect); reruns the same load logic
          // directly instead.
          action={
            <Button color="inherit" size="small" onClick={() => void loadFlows()}>
              Try again
            </Button>
          }
        >
          {error}
        </Alert>
        {renameNoticeSnackbar}
      </Box>
    );
  }

  if (flows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {channelBusinessRules?.trim()
            ? 'No flows could be generated from this channel\'s business rules yet.'
            : 'Add business rules to this channel first, then reopen this tab.'}
        </Typography>
        {renameNoticeSnackbar}
      </Box>
    );
  }

  const selectedFlow = flows.find((f) => f.id === selectedFlowId) ?? flows[0];

  // FIX 11b — a rail row-click swaps this whole flow out (RuleFlowCanvas remounts via
  // key={flow.id}), which would silently drop any unsaved structural edit on the flow being
  // left; route it through the same guard the parent's tab-switch/drawer-close now use.
  const handleSelectFlow = (newFlowId: string) => {
    if (newFlowId === selectedFlow.id) return;
    guardNavigation(() => setSelectedFlowId(newFlowId));
  };

  return (
    // minHeight (matching LegacyWorkflowCanvasTab's own outermost container) guards against a
    // small flow (e.g. a single node, no edges) collapsing this whole row down to its content's
    // intrinsic height instead of filling the tab panel — height: '100%' alone only works when
    // every ancestor already resolves to a definite height, which isn't guaranteed here.
    <Box sx={{ display: 'flex', height: '100%', minHeight: 600 }}>
      <RuleFlowRail
        flows={flows}
        selectedFlowId={selectedFlow.id}
        onSelectFlow={handleSelectFlow}
        // Withheld while a Re-load or a step save is running — same guard Re-load itself uses — so a
        // rename can't be overwritten by the parse result or 404 into a second loader mid-flight.
        onRenameFlow={isReparsing || isEditPersisting ? undefined : handleRenameFlow}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Remounts on flow switch (TDD §8.2: "switching flows is just clicking a different
            rail item; the center canvas swaps in place") — a fresh RuleFlowCanvas instance per
            flow.id guarantees no stale nodes/edges/selection leak between flows. Re-load is a
            channel-level action (re-parses every flow), but its button now lives in the canvas
            header rather than the rail — passed through here alongside the flow it's visually
            anchored to. */}
        <RuleFlowCanvas
          key={selectedFlow.id}
          ref={canvasRef}
          channelId={channelId}
          flow={selectedFlow}
          onPersistingChange={setIsEditPersisting}
          onReparse={handleReparse}
          isReparsing={isReparsing || isEditPersisting}
          onBusinessRulesChanged={onBusinessRulesChanged}
          onDirtyChange={(_flowId, isDirty) => {
            isDirtyRef.current = isDirty;
          }}
          flowRun={flowRunsByFlowId[selectedFlow.id]}
          onOpenDashboard={onOpenDashboard}
        />
      </Box>

      {renameNoticeSnackbar}

      {/* FIX 11b — gates the rail row-click above, and (via guardNavigation/hasUnsavedChanges
          exposed on this component's own ref) the drawer's tab-switch and close handlers in
          ChannelSettings.tsx. */}
      <Dialog open={pendingNavigation !== null} onClose={handleGuardCancel}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingSummaries.length > 1
              ? 'You have unsaved changes to these steps:'
              : 'You have an unsaved change to this step:'}
          </Typography>
          <Box component="ul" sx={{ my: 1, pl: 3 }}>
            {pendingSummaries.map((label, index) => (
              // Node names aren't unique-guaranteed, but the order/count within one guard
              // popup never changes after it opens, so index is a stable, safe key here.
              <Typography key={index} component="li" variant="body2" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2">Save before leaving?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGuardCancel} disabled={isGuardSaving}>
            Cancel
          </Button>
          <Button onClick={handleGuardDiscard} disabled={isGuardSaving}>
            Discard &amp; continue
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleGuardSave()}
            disabled={isGuardSaving}
            startIcon={isGuardSaving ? <CircularProgress size={14} /> : undefined}
          >
            {isGuardSaving ? 'Saving…' : 'Save & continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ChannelRuleFlowsView.displayName = 'ChannelRuleFlowsView';

export default ChannelRuleFlowsView;
